import {
  CrudFilters,
  CrudOperators,
  CrudSorting,
  DataProvider as IDataProvider,
  HttpError,
} from "@pankod/refine-core";
import axios, { AxiosInstance } from "axios";
import { stringify, parse } from "qs";

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
    };

    return Promise.reject(customError);
  }
);

const mapOperator = (operator: CrudOperators) => {
  switch (operator) {
    case "nin":
      return "notIn";
    case "ncontains":
      return "notContains";
    case "containss":
      return "containsi";
    case "ncontainss":
      return "notContainsi";
  }

  return operator;
};

const generateSort = (sort?: CrudSorting) => {
  const _sort: string[] = [];

  if (sort) {
    sort.forEach((item) => {
      if (item.order) {
        _sort.push(`${item.field}:${item.order}`);
      }
    });
  }

  return _sort;
};

const generateFilter = (filters?: CrudFilters) => {
  let rawQuery = "";

  if (filters) {
    filters.forEach(({ field, operator, value }) => {
      const mapedOperator = mapOperator(operator);
      if (field === "_q") return (rawQuery += `&${field}=${value}`);

      if (Array.isArray(value)) {
        value.forEach((val: string) => {
          rawQuery += `&filters${field}[$${mapedOperator}]=${val}`;
        });
      } else {
        const fieldArray = field.split(".").map((ele) => {
          return `[${ele}]`;
        });
        switch (mapedOperator) {
          case "eq":
            rawQuery += `&filters${fieldArray}=${value}`;
            break;
          case "null":
            if (value) {
              rawQuery += `&filters${fieldArray}[$null]`;
            } else {
              rawQuery += `&filters${fieldArray}[$notNull]`;
            }
            break;

          default:
            rawQuery += `&filters${fieldArray}[$${mapedOperator}]=${value}`;
            break;
        }
      }
    });
  }

  const parsedQuery = parse(rawQuery);
  const queryFilters = stringify(parsedQuery, { encodeValuesOnly: true });

  return queryFilters;
};

const normalizeData = (data: any) => {
  const _data = data.data.map((item: any) => ({
    id: item.id,
    ...item.attributes,
  }));

  return _data;
};

export const DataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): IDataProvider => ({
  getList: async ({ resource, pagination, filters, sort, metaData }) => {
    const url = `${apiUrl}/${resource}`;

    const current = pagination?.current || 1;
    const pageSize = pagination?.pageSize || 10;
    const locale = metaData?.locale;
    const fields = metaData?.fields;
    const populate = metaData?.populate;
    const publicationState = metaData?.publicationState;

    const quertSorters = generateSort(sort);
    const queryFilters = generateFilter(filters);

    const query = {
      "pagination[page]": current,
      "pagination[pageSize]": pageSize,
      locale,
      publicationState,
      fields,
      populate,
      sort: quertSorters.length > 0 ? quertSorters.join(",") : undefined,
    };

    const { data } = await httpClient.get(
      `${url}?${stringify(query, {
        encodeValuesOnly: true,
      })}&${queryFilters}`
    );

    return {
      data: normalizeData(data),
      total: data.meta.pagination.total,
    };
  },

  getMany: async ({ resource, ids }) => {
    const url = `${apiUrl}/${resource}`;

    const query = ids
      .map((item: string) => `filters[id][$in]=${item}`)
      .join("&");

    const { data } = await httpClient.get(`${url}?${query}`);

    return {
      data: normalizeData(data),
    };
  },

  create: async ({ resource, variables }) => {
    const url = `${apiUrl}/${resource}`;

    let dataVariables = { data: variables };

    if (resource === "users") {
      dataVariables = variables as any;
    }

    const { data } = await httpClient.post(url, dataVariables);
    return {
      data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    let dataVariables = { data: variables };

    if (resource === "users") {
      dataVariables = variables as any;
    }

    const { data } = await httpClient.put(url, dataVariables);
    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const response = await Promise.all(
      ids.map(async (id) => {
        const url = `${apiUrl}/${resource}/${id}`;

        let dataVariables = { data: variables };

        if (resource === "users") {
          dataVariables = variables as any;
        }
        const { data } = await httpClient.put(url, dataVariables);
        return data;
      })
    );

    return { data: response };
  },

  createMany: async ({ resource, variables }) => {
    const response = await Promise.all(
      variables.map(async (param) => {
        const { data } = await httpClient.post(`${apiUrl}/${resource}`, {
          data: param,
        });
        return data;
      })
    );

    return { data: response };
  },

  getOne: async ({ resource, id, metaData }) => {
    const locale = metaData?.locale;
    const fields = metaData?.fields;
    const populate = metaData?.populate;

    const query = {
      locale,
      fields,
      populate,
    };

    const url = `${apiUrl}/${resource}/${id}?${stringify(query, {
      encode: false,
    })}`;

    const { data } = await httpClient.get(url);

    return {
      data: {
        id: data.data.id,
        ...data.data.attributes,
      },
    };
  },

  deleteOne: async ({ resource, id }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { data } = await httpClient.delete(url);

    return {
      data,
    };
  },

  deleteMany: async ({ resource, ids }) => {
    const response = await Promise.all(
      ids.map(async (id) => {
        const { data } = await httpClient.delete(`${apiUrl}/${resource}/${id}`);
        return data;
      })
    );
    return { data: response };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({ url, method, filters, sort, payload, query, headers }) => {
    let requestUrl = `${url}?`;

    if (sort) {
      const sortQuery = generateSort(sort);
      if (sortQuery.length > 0) {
        requestUrl = `${requestUrl}&${stringify({
          sort: sortQuery.join(","),
        })}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${filterQuery}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload);
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url);
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});
